import { Button, Col, Row } from 'antd';
import React, { useEffect } from 'react';
import { useState } from 'react';
import cx from 'classnames';
import { isNil } from 'lodash';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import RehireAssignmentModal from '../RehireActionSelectionModal/RehireAssignmentModal';
import {
  getApprovedClientsByMember,
  getMyApprovedClients,
} from 'redux/reducers/clientSlice';
import { getMemberPriceAgreement } from 'redux/reducers/recruiterSlice';
import { getPricingAgreement } from 'redux/reducers/accountSlice';
import {
  AssignmentEditUserResponseModel,
  BOUsersPriceAgreementRespModel,
  CustomerResponseModel,
} from 'api/backofficeApiClient';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  isAdminSelector,
  isCorporateTASelector,
  userSelector,
} from 'redux/selectors/authenticationSelectors';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { AssignmentEditAdminResponseModel } from 'api/adminApiClient';
import { getRenewInfoForAssignment } from 'redux/reducers/assignmentSlice';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';

import styles from '../RehireActionSelectionModal/RehireTypeSelectionModal.module.less';

export enum RehireTypeEnum {
  SameCustomer = 1,
  NewCustomer = 2,
}

type Props = {
  onSubmit: () => Promise<void>;
  onClose: () => void;
  assignmentId: string;
  hasRehireDateRestriction: boolean;
};

export default function RehireTypeSelectionModal(props: Props) {
  const { onClose, onSubmit, assignmentId, hasRehireDateRestriction } = props;
  const [activeRehireType, setActiveRehireType] = useState<RehireTypeEnum>();
  const [customers, setCustomers] = useState<CustomerResponseModel[]>([]);
  const [priceAgreement, setPriceAgreement] =
    useState<BOUsersPriceAgreementRespModel>();
  const [isRehireAssignmentModalVisible, setIsRehireAssignmentModalVisible] =
    useState(false);
  const [rehireAssignmentInfo, setRehireAssignmentInfo] = useState<
    AssignmentEditUserResponseModel | AssignmentEditAdminResponseModel
  >();
  const [isCustomersLoaded, setIsCustomersLoaded] = useState(false);
  const [isDataForRehireLoading, setIsDataForRehireLoading] = useState(false);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const user = useAppSelector(userSelector);
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);

  const handleOpenRehireAssignmentModal = () => {
    setIsRehireAssignmentModalVisible(true);
  };

  const handleCloseRehireAssignmentModal = () => {
    onClose();
    setIsRehireAssignmentModalVisible(false);
  };

  useEffect(() => {
    loadPriceAgreement();
    loadCustomers();
    // eslint-disable-next-line
  }, []);

  const loadCustomers = async () => {
    if (isCorporateTA) return;
    const correctAction: any = isAdminRole
      ? getApprovedClientsByMember
      : getMyApprovedClients;

    const result = await dispatch(
      correctAction(isAdminRole ? user?.companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setCustomers(result.payload);
    }
    setIsCustomersLoaded(true);
  };

  const loadPriceAgreement = async () => {
    const correctAction: any = isAdminRole
      ? getMemberPriceAgreement
      : getPricingAgreement;

    const result = await dispatch(
      correctAction(isAdminRole ? user?.companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setPriceAgreement(result.payload);
    }
  };

  const handleSubmitWithChanges = async () => {
    setIsDataForRehireLoading(true);
    const result = await dispatch(
      getRenewInfoForAssignment({
        assignmentId,
        isAdminRole: isAdminRole as boolean,
      }),
    );

    if (getRenewInfoForAssignment.fulfilled.match(result)) {
      setRehireAssignmentInfo(result.payload);
      handleOpenRehireAssignmentModal();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsDataForRehireLoading(false);
  };

  return (
    <>
      {isRehireAssignmentModalVisible && rehireAssignmentInfo && (
        <RehireAssignmentModal
          onSubmit={onSubmit}
          onClose={handleCloseRehireAssignmentModal}
          initialRehireAssignmentInfoValues={rehireAssignmentInfo}
          priceAgreement={priceAgreement}
          customers={customers}
          activeRehireType={activeRehireType}
          isCustomersLoaded={isCustomersLoaded}
          hasRehireDateRestriction={hasRehireDateRestriction}
        />
      )}

      <BaseModal
        title="Select rehire type"
        open={true}
        onCancel={onClose}
        destroyOnClose
        maxWidth={570}
      >
        <div className={cx('modal-scroll-wrapper')}>
          <div className={styles.rehireText}>
            Select your Customer preferences below
          </div>

          <Row gutter={[20, 20]}>
            <Col xs={24} md={12}>
              <div
                className={cx(styles.card, {
                  [styles.isActive]:
                    activeRehireType === RehireTypeEnum.SameCustomer,
                })}
              >
                <Button
                  onClick={() =>
                    setActiveRehireType(RehireTypeEnum.SameCustomer)
                  }
                >
                  The same customer
                </Button>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <div
                className={cx(styles.card, {
                  [styles.isActive]:
                    activeRehireType === RehireTypeEnum.NewCustomer,
                })}
              >
                <Button
                  type="primary"
                  onClick={() =>
                    setActiveRehireType(RehireTypeEnum.NewCustomer)
                  }
                >
                  A new customer
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        <BaseModalFooter
          className={cx('button-space-between', styles.rehireFooter)}
        >
          <Button className="modal-icon-btn d-xs-none" onClick={onClose}>
            <span className="icon icon-closed-outlined" />
            Cancel
          </Button>

          <Button
            type="primary"
            disabled={isDataForRehireLoading || isNil(activeRehireType)}
            onClick={handleSubmitWithChanges}
          >
            Next
          </Button>
        </BaseModalFooter>
      </BaseModal>
    </>
  );
}
